import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, FileText, ArrowRightLeft, Download } from "lucide-react";
import { toast } from "sonner";

const empty = { customer_id: "", customer_name: "", builder: "", site_address: "", scope: "",
  line_items: [{ description: "", qty: 1, unit_price: 0 }], gst_rate: 10, status: "draft", notes: "" };

const statusColor = { draft: "bg-slate-200 text-slate-700", sent: "bg-blue-100 text-blue-700", accepted: "bg-emerald-100 text-emerald-700" };
const money = (n) => `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function buildCatalog(pricing) {
  if (!pricing) return [];
  const out = [];
  (pricing.sections || []).forEach((s) => {
    (s.items || []).forEach((it) => out.push({ label: `${s.title} ${it.size}`, price: it.price, min_lm: s.min_lm || 0, unit: "lm" }));
    (s.external || []).forEach((it) => out.push({ label: `${s.title} (External) ${it.size}`, price: it.price, min_lm: s.min_lm || 0, unit: "lm" }));
  });
  (pricing.materials || []).forEach((m) => out.push({ label: m.name, price: m.price, min_lm: 0, unit: "" }));
  return out;
}

function QuoteForm({ open, setOpen, quote, customers, catalog, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(quote ? { ...empty, ...quote } : empty); }, [quote, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm((f) => ({ ...f, customer_id: id, customer_name: c?.name || f.customer_name, builder: c?.builder || f.builder, site_address: c?.address || f.site_address }));
  };
  const setItem = (i, k, v) => setForm((f) => { const li = [...f.line_items]; li[i] = { ...li[i], [k]: k === "description" ? v : parseFloat(v) || 0 }; return { ...f, line_items: li }; });
  const addItem = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", qty: 1, unit_price: 0 }] }));
  const addFromCatalog = (idx) => { const c = catalog[idx]; if (!c) return; setForm((f) => ({ ...f, line_items: [...f.line_items, { description: c.label, qty: c.min_lm || 1, unit_price: c.price, min_lm: c.min_lm || 0, unit: c.unit }] })); };
  const removeItem = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, x) => x !== i) }));

  const subtotal = form.line_items.reduce((s, li) => s + (li.qty || 0) * (li.unit_price || 0), 0);
  const gst = subtotal * (form.gst_rate || 0) / 100;
  const total = subtotal + gst;

  const save = async () => {
    setSaving(true);
    try {
      if (quote) await api.put(`/quotes/${quote.id}`, form);
      else await api.post("/quotes", form);
      toast.success("Quote saved"); setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{quote ? "Edit Quote" : "New Quote"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Customer</Label>
            <Select value={form.customer_id || undefined} onValueChange={onCustomer}>
              <SelectTrigger data-testid="quote-customer-select" className="mt-1"><SelectValue placeholder="— Select customer —" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="quote-status-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="sent">Sent</SelectItem><SelectItem value="accepted">Accepted</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Customer Name *</Label><Input data-testid="quote-name-input" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="mt-1" /></div>
          <div><Label>Builder</Label><Input value={form.builder} onChange={(e) => set("builder", e.target.value)} className="mt-1" /></div>
          <div className="sm:col-span-2"><Label>Site Address</Label><Input value={form.site_address} onChange={(e) => set("site_address", e.target.value)} className="mt-1" /></div>
          <div className="sm:col-span-2"><Label>Scope of Works</Label><Textarea value={form.scope} onChange={(e) => set("scope", e.target.value)} rows={2} className="mt-1" /></div>
        </div>

        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <Label>Line Items</Label>
            <div className="flex items-center gap-3">
              <Select value={undefined} onValueChange={(v) => addFromCatalog(Number(v))}>
                <SelectTrigger data-testid="catalog-select" className="h-8 w-52 text-xs"><SelectValue placeholder="+ Add from Price List" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {(catalog || []).map((c, i) => <SelectItem key={i} value={String(i)} data-testid={`catalog-option-${i}`}>{c.label} — ${c.price}{c.unit === "lm" ? "/lm" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              <button data-testid="add-line-item" onClick={addItem} className="text-sm text-blue-700 font-medium flex items-center gap-1"><Plus className="w-4 h-4" />Add item</button>
            </div>
          </div>
          <div className="space-y-2">
            {form.line_items.map((li, i) => (
              <div key={i}>
                <div className="flex gap-2 items-center">
                  <Input data-testid={`li-desc-${i}`} placeholder="Description" value={li.description} onChange={(e) => setItem(i, "description", e.target.value)} className="flex-1" />
                  <Input data-testid={`li-qty-${i}`} type="number" placeholder="Qty" value={li.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="w-20" />
                  <Input data-testid={`li-price-${i}`} type="number" placeholder="Unit $" value={li.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} className="w-28" />
                  <span className="w-24 text-right text-sm font-medium text-navy">{money((li.qty || 0) * (li.unit_price || 0))}</span>
                  <button onClick={() => removeItem(i)} className="text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
                {li.min_lm > 0 && (li.qty || 0) < li.min_lm && (
                  <p data-testid={`li-min-warning-${i}`} className="text-xs text-amber-700 mt-1 ml-1">⚠ Under {li.min_lm}lm minimum — half/full day rate may apply.</p>
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between text-slate-500"><span>Subtotal</span><span data-testid="quote-subtotal">{money(subtotal)}</span></div>
              <div className="flex justify-between text-slate-500 items-center"><span>GST
                <Input type="number" value={form.gst_rate} onChange={(e) => set("gst_rate", parseFloat(e.target.value) || 0)} className="inline-block w-14 h-6 mx-1 px-1 text-xs" />%</span><span>{money(gst)}</span></div>
              <div className="flex justify-between font-heading font-bold text-navy text-base border-t border-slate-200 pt-1"><span>Total</span><span data-testid="quote-total">{money(total)}</span></div>
            </div>
          </div>
        </div>

        <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1" /></div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="quote-save-button" onClick={save} className="bg-navy hover:bg-navy-light" disabled={!form.customer_name || saving}>{saving ? "Saving…" : "Save Quote"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Quotes() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const navigate = useNavigate();

  const load = () => {
    api.get("/quotes").then((r) => setItems(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/pricing").then((r) => setCatalog(buildCatalog(r.data))).catch(() => {});
  };
  useEffect(load, []);

  const matchesStatus = (q) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "pending") return q.status === "draft" || q.status === "sent";
    return q.status === statusFilter;
  };
  const shown = items.filter(matchesStatus);

  const del = async (id) => { if (!window.confirm("Delete quote?")) return; await api.delete(`/quotes/${id}`); toast.success("Deleted"); load(); };
  const convert = async (id) => {
    if (!window.confirm("Convert this quote into a scheduled job?")) return;
    try {
      const r = await api.post(`/quotes/${id}/convert`);
      toast.success(`Job ${r.data.job_number} created`); load(); navigate(`/jobs/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const downloadPdf = async (q) => {
    try {
      const res = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${q.quote_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate PDF"); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Quotes</h1><p className="text-slate-500 mt-1">{shown.length} of {items.length} quotes</p></div>
        <Button data-testid="new-quote-button" onClick={() => { setEditing(null); setOpen(true); }} className="bg-navy hover:bg-navy-light"><Plus className="w-4 h-4 mr-2" />New Quote</Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {["all", "pending", "draft", "sent", "accepted"].map((s) => (
          <button key={s} data-testid={`quote-filter-${s}`} onClick={() => setStatusFilter(s)}
            className={`px-3 py-2 rounded-lg text-sm font-medium capitalize ${statusFilter === s ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{s}</button>
        ))}
      </div>

      <div className="grid gap-3">
        {shown.map((q) => (
          <div key={q.id} data-testid={`quote-row-${q.id}`} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-slate-400">{q.quote_number}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${statusColor[q.status]}`}>{q.status}</span>
                {q.job_id && <span className="text-xs text-emerald-600 font-medium">→ Job created</span>}
                {q.created_at && <span className="text-xs text-slate-400">· {formatDate(q.created_at)}</span>}
              </div>
              <p className="font-semibold text-navy mt-1 truncate">{q.customer_name}</p>
              <p className="text-sm text-slate-500 truncate">{q.site_address || q.scope}</p>
            </div>
            <div className="text-right"><p className="text-xl font-heading font-bold text-navy">{money(q.total)}</p><p className="text-xs text-slate-400">inc. GST</p></div>
            <div className="flex gap-1">
              <button data-testid={`quote-pdf-${q.id}`} onClick={() => downloadPdf(q)} title="Download PDF" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><Download className="w-4 h-4" /></button>
              {!q.job_id && <button data-testid={`quote-convert-${q.id}`} onClick={() => convert(q.id)} title="Convert to Job" className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-600"><ArrowRightLeft className="w-4 h-4" /></button>}
              <button data-testid={`quote-edit-${q.id}`} onClick={() => { setEditing(q); setOpen(true); }} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`quote-delete-${q.id}`} onClick={() => del(q.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <p className="text-slate-400 text-center py-12">No quotes yet. Create your first quote.</p>}
      </div>

      <QuoteForm open={open} setOpen={setOpen} quote={editing} customers={customers} catalog={catalog} onSaved={load} />
    </div>
  );
}
